import { useState } from "react";
import type { TokenizedWord } from "@/lib/types";
import { Card, CardContent } from "@/components/ui/card";
import { ClickableCharacters } from "./ClickableCharacters";

interface TokenCardProps {
  token: TokenizedWord;
  onCharacterClick?: (character: string) => void;
}

export function TokenCard({ token, onCharacterClick }: TokenCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasEntries = token.entries.length > 0;
  const hasMultipleEntries = token.entries.length > 1;

  // Get first entry for summary display
  const firstEntry = token.entries[0];

  return (
    <Card
      className={`overflow-hidden transition-all ${
        hasEntries && hasMultipleEntries ? "cursor-pointer hover:shadow-md" : ""
      } ${!hasEntries ? "opacity-60" : ""} ${isExpanded ? "ring-2 ring-primary" : ""}`}
      onClick={() => hasMultipleEntries && setIsExpanded(!isExpanded)}
    >
      <CardContent className="p-4">
        <div className="text-4xl font-medium mb-2">
          <ClickableCharacters text={token.text} onCharacterClick={onCharacterClick} />
        </div>
        {hasEntries ? (
          <>
            <div className="text-lg text-blue-600 dark:text-blue-400 font-mono mb-3">
              {firstEntry.jyutping}
            </div>
            <ul className="list-disc list-inside text-muted-foreground space-y-1">
              {firstEntry.definitions.map((def, i) => (
                <li key={i} className="text-sm">
                  {def}
                </li>
              ))}
            </ul>
            {hasMultipleEntries && (
              <div className="text-xs text-primary mt-3">
                +{token.entries.length - 1} more meaning{token.entries.length - 1 !== 1 ? "s" : ""} {isExpanded ? "▲" : "▼"}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground">Not found in dictionary</div>
        )}
      </CardContent>

      {isExpanded && hasMultipleEntries && (
        <div className="border-t border-border p-4 bg-muted/30 space-y-4">
          {token.entries.slice(1).map((entry, i) => (
            <div key={i} className="border-b border-border last:border-b-0 pb-4 last:pb-0">
              <div className="text-lg text-blue-600 dark:text-blue-400 font-mono mb-2">
                {entry.jyutping}
              </div>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                {entry.definitions.map((def, j) => (
                  <li key={j} className="text-sm">
                    {def}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
